import { assertNever } from "@redotech/util/type";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import { CustomsDeclarationSchema } from "../fulfillments/customs-declaration";
import {
  Carriers,
  CarrierSchema,
  ServiceSchema,
} from "../fulfillments/fulfillment-carriers-and-services";
import {
  FulfillmentOrderAddressSchema,
  VerifiedAddressSchema,
} from "../fulfillments/fulfillment-order-address";
import { Hazmat } from "../fulfillments/hazmat";
import { ContentsType, NonDeliveryOption, USPSExemptionCode } from "./customs";
import { OutboundParcelSchema } from "./parcel";
import { WeightSchema } from "./util";

// Some info on payment responsibilities: https://www.ups.com/assets/resources/webcontent/en_GB/UPS_Payment_Options.pdf
export enum PaymentResponsibility {
  TRANSPORTATION = "TRANSPORTATION",
  DUTIES_AND_TAXES = "DUTIES_AND_TAXES",
  VAT = "VAT",
}

export enum ShipmentPaymentOptionType {
  SENDER = "SENDER",
  THIRD_PARTY = "THIRD_PARTY",
  RECEIVER = "RECEIVER",
  COLLECT = "COLLECT",
}

export enum UspsBillingType {
  REDO = "redo",
  MERCHANT = "merchant",
}

const BasePaymentOptionSchema = z.object({
  explicit_payment_responsibilities: z
    .array(
      createEnum(PaymentResponsibility, { enumName: "PaymentResponsibility" }),
    )
    .nullish(),
});

const SenderPaymentOptionSchema = BasePaymentOptionSchema.extend({
  type: z.literal(ShipmentPaymentOptionType.SENDER),
});

const ThirdPartyPaymentOptionSchema = BasePaymentOptionSchema.extend({
  type: z.literal(ShipmentPaymentOptionType.THIRD_PARTY),
  carrier: createEnum(Carriers, { enumName: "Carriers" }), // Not part of the easypost schema, but needed to determine which rates to return
  account: z.string(),
  country: z.string(),
  postal_code: z.string(),
});

export type ThirdPartyPaymentOption = z.infer<
  typeof ThirdPartyPaymentOptionSchema
>;

const ReceiverPaymentOptionSchema = BasePaymentOptionSchema.extend({
  type: z.literal(ShipmentPaymentOptionType.RECEIVER),
  account: z.string(),
  postal_code: z.string(),
});

const CollectPaymentOptionSchema = BasePaymentOptionSchema.extend({
  type: z.literal(ShipmentPaymentOptionType.COLLECT),
});

export const ShipmentPaymentOptionSchema = z.discriminatedUnion("type", [
  SenderPaymentOptionSchema,
  ThirdPartyPaymentOptionSchema,
  ReceiverPaymentOptionSchema,
  CollectPaymentOptionSchema,
]);

export function isPaymentSplitDutyVat(
  paymentOption: ShipmentPaymentOption | null | undefined,
): boolean {
  if (!paymentOption) {
    return false;
  }
  return (
    paymentOption.type === ShipmentPaymentOptionType.THIRD_PARTY &&
    paymentOption.explicit_payment_responsibilities?.length === 1 &&
    paymentOption.explicit_payment_responsibilities?.[0] ===
      PaymentResponsibility.VAT
  );
}

export function isThirdPartyOnlyPayingVat(
  paymentOption: ShipmentPaymentOption | null | undefined,
): boolean {
  if (!paymentOption) {
    return false;
  }
  return !!(
    paymentOption.type === ShipmentPaymentOptionType.THIRD_PARTY &&
    paymentOption.explicit_payment_responsibilities?.includes(
      PaymentResponsibility.VAT,
    )
  );
}

export type ShipmentPaymentOption = z.infer<typeof ShipmentPaymentOptionSchema>;
export interface IShipmentPaymentOption
  extends z.ZodType<ShipmentPaymentOption> {}

export enum ShipmentRefundStatus {
  Submitted = "submitted",
  Refunded = "refunded",
  Rejected = "rejected",
  NotApplicable = "not_applicable",
  AlreadyShipped = "already_shipped", // A redo status
}

export const IB_CARRIER_ACCOUNT_ID = "IB";

export enum RateProvider {
  SOAP_BOX = "SOAP_BOX",
  EASYPOST = "EASYPOST",
  UPS = "UPS",
  LANDMARK_GLOBAL = "LANDMARK_GLOBAL",
  REDO_LANDMARK_GLOBAL = "REDO_LANDMARK_GLOBAL",
  INTERNATIONAL_BRIDGE = "INTERNATIONAL_BRIDGE",
  ETA_EXPRESS = "ETA_EXPRESS",
  TEST_CARRIER = "TEST_CARRIER",
  FEDEX = "FEDEX",
  USPS = "USPS",
  DHL_ECOMMERCE = "DHL_ECOMMERCE",
  GLOBALE = "GLOBALE",
  TIKTOK = "TIKTOK",
  CANADA_POST = "CanadaPost",
}

export const rateTableProviderLabel: Record<RateProvider, string> = {
  [RateProvider.SOAP_BOX]: "Soap Box",
  [RateProvider.EASYPOST]: "EasyPost",
  [RateProvider.UPS]: "UPS",
  [RateProvider.LANDMARK_GLOBAL]: "Landmark Global",
  [RateProvider.REDO_LANDMARK_GLOBAL]: "Redo Landmark Global",
  [RateProvider.INTERNATIONAL_BRIDGE]: "International Bridge",
  [RateProvider.ETA_EXPRESS]: "ETA Express",
  [RateProvider.TEST_CARRIER]: "Test Carrier",
  [RateProvider.FEDEX]: "FedEx",
  [RateProvider.USPS]: "USPS",
  [RateProvider.DHL_ECOMMERCE]: "DHL eCommerce",
  [RateProvider.CANADA_POST]: "Canada Post",
  [RateProvider.GLOBALE]: "Global-E",
  [RateProvider.TIKTOK]: "TikTok",
};

export const ShipmentMessageSchema = z.object({
  carrier: z.string().nullish(),
  carrier_account_id: z.string().nullish(),
  message: z.string().nullish(),
  type: z.string().nullish(),
  ignored: z.boolean().nullish(),
});
export type ShipmentMessage = z.infer<typeof ShipmentMessageSchema>;

export const ProviderInfoSchema = z.object({
  soapBox: z
    .object({
      orderId: z.string(),
      packageId: z.string(),
      externalOrderNumber: z.string().nullish(),
      batchId: z.string().nullish(),
    })
    .nullish(),
  ups: z.object({ shipperId: z.string() }).nullish(),
  landmarkGlobal: z.object({ username: z.string() }).nullish(),
  easypost: z.object({ childAccountId: z.string() }).nullish(),
  usps: z
    .object({
      rateIndicator: z.string().nullish(),
      processingCategory: z.string().nullish(),
      destinationEntryFacilityType: z.string().nullish(),
    })
    .nullish(),
  fedex: z
    .object({ accountNumber: z.string(), packagingType: z.string().nullish() })
    .nullish(),
  dhlEcommerce: z
    .object({
      pickupId: z.string(),
      distributionCenter: z.string(),
      redoPackageId: z.string(),
    })
    .nullish(),
  globale: z
    .object({
      globalEOrderId: z.string(),
      lineItems: z.array(z.object({ sku: z.string(), quantity: z.number() })),
    })
    .nullish(),
  tiktok: z
    .object({ shippingServiceId: z.string(), tiktokShopId: z.string() })
    .nullish(),
});

// This is information necessary for Redo rate logic that is not provided by the shipping provider
// on a rate refresh. This information will not change if the shipment has not changed.
export const SupplementalRefreshInfoSchema = z.object({
  fedex_zone: z.number(),
  usps_zone: z.number(),
  saturday_delivery: z.boolean(),
  is_merchant_carrier_account: z.boolean(),
  is_parent_easypost_account: z.boolean().nullish(),
  providerInfo: ProviderInfoSchema.nullish(),
});
export type SupplementalRefreshInfo = z.infer<
  typeof SupplementalRefreshInfoSchema
>;

export const rawOutboundRateSchema = z.object({
  id: z.string(),
  shipment_id: z.string(),
  currency: z.string(),
  carrier: CarrierSchema,
  service: ServiceSchema,
  delivery_date: z.string().nullish(),
  delivery_days: z.number().nullish(),
  delivery_date_guaranteed: z.boolean().nullish(),
  created_at: z.string(),
  updated_at: z.string(),
  carrier_account_id: z.string(),
  billing_type: z.string(),
  provider_cost: z.string(),
  rev_share_margin: z.string().nullish(), // This does not factor into the rate we or the merchant pays, but is used for competitive spread and reporting
  fedex_zone: z.number(),
  usps_zone: z.number(),
  ups_zone: z.number().nullish(),
  mode: z.string(),
  refund_status: createEnum(ShipmentRefundStatus, {
    enumName: "ShipmentRefundStatus",
  }).nullish(),
  saturday_delivery: z.boolean(),
  provider: createEnum(RateProvider, { enumName: "RateProvider" }).default(
    RateProvider.EASYPOST,
  ),
  is_merchant_carrier_account: z.boolean(),
  is_parent_easypost_account: z.boolean().nullish(),
  payment_option: ShipmentPaymentOptionSchema.nullish(),
  providerInfo: ProviderInfoSchema.nullish(),
});

export type RawOutboundRate = z.infer<typeof rawOutboundRateSchema>;

const RateSchema_ = rawOutboundRateSchema.extend({
  rate: z.string(),
  competitive_spread_applied: z.boolean().nullish(),
  redo_competitive_spread_applied: z.boolean().nullish(),
  rev_share_margin: z.string().nullish(),
  automation_priority_rate: z.boolean().nullish(),
  provider_cost: z.string().nullish(), // Nullish for backwards compatibility
  fedex_zone: z.number().nullish(), // Nullish for backwards compatibility
  usps_zone: z.number().nullish(), // Nullish for backwards compatibility
  ups_zone: z.number().nullish(), // Nullish for backwards compatibility
  mode: z.string().nullish(), // Nullish for backwards compatibility
  is_merchant_carrier_account: z.boolean().nullish(), // Nullish for backwards compatibility
  is_parent_easypost_account: z.boolean().nullish(), // Nullish for backwards compatibility
  billing_type: z.string().nullish(), // Nullish for backwards compatibility
  saturday_delivery: z.boolean().nullish(), // Nullish for backwards compatibility
});

export const RateSchema = RateSchema_.extend({
  multiParcelRates: z.array(RateSchema_).nullish(),
});

export type OutboundRate = z.infer<typeof RateSchema>;

export const CheckoutOutboundRateSchema = RateSchema.pick({
  provider_cost: true,
  rate: true,
  carrier: true,
  service: true,
  delivery_days: true,
  currency: true,
});
export type CheckoutOutboundRate = z.infer<typeof CheckoutOutboundRateSchema>;

export const OutboundLabelShippingAddressSchema = z.object({
  name: z.string().nullish(),
  address1: z.string(),
  address2: z.string().nullish(),
  city: z.string(),
  country: z.string().nullish(),
  country_code: z.string().nullish(),
  province: z.string().nullish(),
  province_code: z.string().nullish(),
  zip: z.string().nullish(),
  first_name: z.string().nullish(),
  last_name: z.string().nullish(),
  company: z.string().nullish(),
  email: z.string().nullish(),
  phone: z.string(),
});

export enum SpecialRateEligibility {
  MEDIA_MAIL = "USPS.MEDIAMAIL",
  LIBRARY_MAIL = "USPS.LIBRARYMAIL",
  MEDIA_MAIL_AND_LIBRARY_MAIL = "USPS.MEDIAMAIL,USPS.LIBRARYMAIL",
  DHL_BPM = "DHL.BPM",
}

export function includesMediaMail(
  eligibility: SpecialRateEligibility | undefined | null,
): boolean {
  return (
    eligibility === SpecialRateEligibility.MEDIA_MAIL ||
    eligibility === SpecialRateEligibility.MEDIA_MAIL_AND_LIBRARY_MAIL
  );
}

export function includesLibraryMail(
  eligibility: SpecialRateEligibility | undefined | null,
): boolean {
  return (
    eligibility === SpecialRateEligibility.LIBRARY_MAIL ||
    eligibility === SpecialRateEligibility.MEDIA_MAIL_AND_LIBRARY_MAIL
  );
}

export function includesDhlBpm(
  eligibility: SpecialRateEligibility | undefined | null,
): boolean {
  return eligibility === SpecialRateEligibility.DHL_BPM;
}

const ShipmentRatesSchema_ = z.object({
  easypostShipmentId: z.string().nullish(),
  easypostOrderId: z.string().nullish(),
  // This is the parcel assigned to a purchased shipment rate
  parcel: OutboundParcelSchema.nullish(),
  // Since we treat multi-parcel shipment rates as a single "Rate", we use this to calculate ratesOutdated
  parcelsUsedToCalculateRate: z.array(OutboundParcelSchema).nullish(),
  mode: z.string(),
  rates: z.array(RateSchema),
  /**  */
  weight: WeightSchema,
  originAddress: z
    .union([FulfillmentOrderAddressSchema, VerifiedAddressSchema])
    .nullish(),
  destinationAddress: z
    .union([FulfillmentOrderAddressSchema, VerifiedAddressSchema])
    .nullish(),
  signatureRequired: z.boolean().nullish(),
  adultSignatureRequired: z.boolean().nullish(),
  specialRateEligibility: createEnum(SpecialRateEligibility, {
    enumName: "SpecialRateEligibility",
  }).nullish(),
  directDeliveryOnly: z.boolean().nullish(),
  carbonNeutral: z.boolean().nullish(),
  nonDeliveryOption: createEnum(NonDeliveryOption, {
    enumName: "NonDeliveryOption",
  }).nullish(),
  ddp: z.boolean().nullish(),
  returnAddress: FulfillmentOrderAddressSchema.nullish(),
  shipmentIds: z.array(z.string()).nullish(),
  shipmentMessages: z
    .record(z.string(), z.array(ShipmentMessageSchema))
    .nullish(),
  createdAt: z.date().nullish(),
  // This info is now stored at the package level
  /** @deprecated */
  itn: z.string().nullish(),
  /** @deprecated */
  dryIceWeight: WeightSchema.nullish(),
  /** @deprecated */
  hazmat: createEnum(Hazmat, { enumName: "Hazmat" }).nullish(),
  /** @deprecated */
  taxIdentifierIds: z.array(z.string()).nullish(),
  /** @deprecated */
  uspsExemptionCode: createEnum(USPSExemptionCode, {
    enumName: "USPSExemptionCode",
  }).nullish(),
  /** @deprecated */
  additionalCustomsComments: z.string().nullish(),
  /** @deprecated */
  contentsExplanation: z.string().nullish(),
  /** @deprecated */
  contentsType: createEnum(ContentsType, {
    enumName: "ContentsType",
  }).nullish(),
  /** @deprecated */
  customsDeclarations: z.array(CustomsDeclarationSchema).nullish(),
  refreshRatesAt: z.date().nullish(),
});

export const ShipmentRatesSchema: z.ZodType<ShipmentRates> =
  ShipmentRatesSchema_;

export interface ShipmentRates extends z.infer<typeof ShipmentRatesSchema_> {}

// If the rate came from a child account (aka it's a merchant rate) but easypost still tells us that it's billed through easypost, we need to charge for the label
export function shouldChargeForLabel(rate: OutboundRate) {
  // Even though we treat all providers the same right now, we want to force developers to consider about this logic when they add a new provider
  // Addint this check ensures we think about this logic when we add a new provider
  switch (rate.provider) {
    case RateProvider.EASYPOST:
    case RateProvider.UPS:
    case RateProvider.LANDMARK_GLOBAL:
    case RateProvider.REDO_LANDMARK_GLOBAL:
    case RateProvider.INTERNATIONAL_BRIDGE:
    case RateProvider.ETA_EXPRESS:
    case RateProvider.SOAP_BOX:
    case RateProvider.FEDEX:
    case RateProvider.TEST_CARRIER:
    case RateProvider.DHL_ECOMMERCE:
    case RateProvider.CANADA_POST:
    case RateProvider.GLOBALE:
      // If a merchant has brought their own parent easypost account, all of the billing is handled outside of redo
      if (rate.is_parent_easypost_account) {
        return false;
      }

      if (rate.payment_option?.type === ShipmentPaymentOptionType.THIRD_PARTY) {
        return false;
      }

      return (
        !rate.is_merchant_carrier_account || rate.billing_type === "easypost"
      );
    case RateProvider.USPS:
      // USPS doesn't support third party billing, but adding this just in case for future
      if (rate.payment_option?.type === ShipmentPaymentOptionType.THIRD_PARTY) {
        return false;
      }

      return rate.billing_type === UspsBillingType.REDO;
    case RateProvider.TIKTOK:
      return false; // We never pay for these labels, so we should never charge for them
    default:
      assertNever(rate.provider);
  }
}

export function isMultiParcelShipmentRates(
  shipmentRates: ShipmentRates,
): boolean {
  return (
    !!shipmentRates.parcelsUsedToCalculateRate &&
    shipmentRates.parcelsUsedToCalculateRate.length > 1
  );
}

export function getShipmentParcels(shipmentRates: ShipmentRates) {
  if (isMultiParcelShipmentRates(shipmentRates)) {
    return shipmentRates.parcelsUsedToCalculateRate!;
  }
  return shipmentRates.parcel ? [shipmentRates.parcel] : [];
}
